import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getBillingStatus } from "../data/request";
import { DELTA_PRIMARY, DELTA_ACCENT, TEXT_SECONDARY } from "./colors";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20000;

export const CheckoutReturnPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    const poll = async () => {
      while (!cancelled) {
        const doc = await getBillingStatus();
        // Only DynamoDB (written by the Stripe webhook) proves the
        // subscription is provisioned - the redirect can land before the
        // webhook has fired, so the ID token's own claim isn't trustworthy yet.
        // Checkout is one product per session, so it's either a web (Pro/
        // Enterprise) purchase or an API (API Pro/Enterprise) purchase -
        // check both, since this page doesn't know which one was bought.
        const webConfirmed = doc?.web_status === "active" && doc?.web_plan !== "free";
        const apiConfirmed = doc?.api_status === "active" && doc?.api_plan !== "none";
        if (doc && (webConfirmed || apiConfirmed)) {
          break;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          if (!cancelled) setTimedOut(true);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
      if (cancelled) return;
      await auth.refreshSession();
      if (!cancelled) navigate("/billing");
    };

    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (timedOut) {
    return (
      <Box style={{ padding: 40, textAlign: "center" }}>
        <Typography variant="h6" style={{ color: DELTA_PRIMARY, fontWeight: 700, marginBottom: 8 }}>
          Still processing
        </Typography>
        <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 24 }}>
          This can take a minute. Refresh this page or check your Billing page shortly.
        </Typography>
        <Button
          variant="contained"
          style={{ backgroundColor: DELTA_ACCENT }}
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box style={{ padding: 40, textAlign: "center" }}>
      <CircularProgress color="primary" style={{ marginBottom: 16 }} />
      <Typography variant="body1" style={{ color: TEXT_SECONDARY }}>
        Confirming your subscription...
      </Typography>
    </Box>
  );
};

export default CheckoutReturnPage;
